import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom'; // 🟢 IMPORTED useNavigate
import { PenTool, BookOpen, Send, Clock, Eye, ThumbsUp, Loader2, CheckCircle2, ImagePlus, X, ImageIcon, Clock4, Trash2, Pencil, ArrowRight } from 'lucide-react'; // 🟢 IMPORTED ArrowRight
import { toast } from 'sonner';

// 🟢 IMPORT YOUR APIs AND HOOKS HERE (Adjust paths based on your folder structure)
import { expertBlogAPI } from '../../api/expertBlogApi'; 
import { useExpertBlogs } from '../../hooks/expertBlogHooks/useExpertBlogs'; 
import { useExpertDashboard } from '../../hooks/expertDashboardHooks/useExpertDashboard'; 

export default function ExpertBlogPage() {
  const navigate = useNavigate(); // 🟢 INITIALIZED navigate
  const { dashboardData } = useExpertDashboard();
  
  const myExpertCode = dashboardData?.expert_code || dashboardData?.expertCode || ''; 
  const myExpertName = dashboardData?.fullName || dashboardData?.expertName || '';
  const categoryId = dashboardData?.categoryId || dashboardData?.category_id || null;
  const subCategoryId = dashboardData?.subCategoryId || dashboardData?.sub_category_id || null;

  const [activeTab, setActiveTab] = useState<'write' | 'history'>('write');

  // Form States
  const [editingBlogId, setEditingBlogId] = useState<number | string | null>(null); // 🟢 Tracks if we are editing an existing post
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingText, setLoadingText] = useState(''); 
  
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 🟢 LIVE FETCH: Custom hook grabbing blogs + delete capability
  const { blogs, setBlogs, isLoadingBlogs, deleteBlog, isDeleting } = useExpertBlogs(myExpertCode);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size must be less than 2MB."); 
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setCoverImage(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setCoverImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // 🟢 Helper to reset form fully
  const resetForm = () => {
    setEditingBlogId(null);
    setTitle('');
    setContent('');
    setCoverImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePostBlog = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!myExpertCode) {
      toast.error("Error: Expert details are still loading. Please wait a second.");
      return;
    }
    
    if (!title.trim() || !content.trim()) return;

    setIsSubmitting(true);

    try {
      let uploadedImageUrl = undefined;

      // Only upload if a NEW image was selected
      if (coverImage) {
        setLoadingText('Uploading image...'); 
        const uploadRes = await expertBlogAPI.uploadCoverImage(coverImage);
        if (uploadRes && uploadRes.url) {
          uploadedImageUrl = uploadRes.url;
        } else {
          throw new Error("Failed to get image URL from server");
        }
      }

      if (editingBlogId) {
        // 🟢 EDIT EXISTING BLOG
        setLoadingText('Updating article...'); 
        const updatedBlog = await expertBlogAPI.updateBlog(editingBlogId, {
          title,
          content,
          categoryId: categoryId,
          subCategoryId: subCategoryId,
          coverImage: uploadedImageUrl // If undefined, backend keeps the old image automatically
        });

        // Update local state smoothly
        setBlogs((prevBlogs: any) => prevBlogs.map((b: any) => b.id === editingBlogId ? updatedBlog : b));
        toast.success('Blog updated successfully and sent for approval!');
      } else {
        // 🟢 CREATE NEW BLOG
        setLoadingText('Submitting for approval...'); 
        const newSavedBlog = await expertBlogAPI.createPost({
          title,
          content,
          expertCode: myExpertCode,
          expertName: myExpertName,
          categoryId: categoryId, 
          subCategoryId: subCategoryId,
          coverImage: uploadedImageUrl,
          blogStatus: 'pending' 
        });

        setBlogs((prevBlogs: any) => [newSavedBlog, ...prevBlogs]);
        toast.success('Blog submitted successfully for Admin approval!'); 
      }
      
      resetForm();
      setActiveTab('history');

    } catch (error) {
      console.error("Error publishing blog:", error);
      toast.error("Failed to submit blog. Please try again.");
    } finally {
      setIsSubmitting(false);
      setLoadingText(''); 
    }
  };

  // 🟢 Edit Handler
  const handleEditClick = (blog: any) => {
    setEditingBlogId(blog.id);
    setTitle(blog.title);
    setContent(blog.content);
    setImagePreview(blog.cover_image || blog.coverImage || null);
    setCoverImage(null); // Clear the file input since it's an existing URL
    setActiveTab('write'); // Jump to the write tab
  };

  // 🟢 Delete Handler
  const handleDeleteClick = async (id: string | number) => {
    if (window.confirm("Are you sure you want to delete this blog post? This cannot be undone.")) {
      await deleteBlog(id);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 lg:p-8 font-sans">
      <div className="max-w-5xl mx-auto space-y-6">
        
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Expert Blog</h1>
          <p className="text-sm text-slate-500 mt-1">Share your knowledge, write articles, and engage with your clients.</p>
        </div>

        <div className="flex items-center gap-2 bg-white p-1.5 rounded-xl border border-slate-200 shadow-sm w-fit">
          <button
            onClick={() => {
              resetForm(); // Clear edit mode if clicked manually
              setActiveTab('write');
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'write' 
                ? 'bg-emerald-50 text-emerald-700 shadow-sm border border-emerald-100' 
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <PenTool className="w-4 h-4" />
            Write a Post
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'history' 
                ? 'bg-blue-50 text-blue-700 shadow-sm border border-blue-100' 
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            My Blogs
          </button>
        </div>

        {activeTab === 'write' && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            {editingBlogId && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-xl text-blue-800 text-sm font-medium flex items-center justify-between">
                <span>You are currently editing an existing post. Saving will again submit post for review.</span>
                <button onClick={resetForm} className="text-blue-600 hover:text-blue-800 underline">Cancel Edit</button>
              </div>
            )}
            
            <form onSubmit={handlePostBlog} className="space-y-6">
              
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Cover Image <span className="text-slate-400 font-normal">(Optional, Max 2MB)</span>
                </label>
                {!imagePreview ? (
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-40 rounded-xl border-2 border-dashed border-slate-300 hover:border-emerald-500 hover:bg-emerald-50 transition-colors flex flex-col items-center justify-center cursor-pointer text-slate-500 group"
                  >
                    <ImagePlus className="w-8 h-8 mb-2 text-slate-400 group-hover:text-emerald-500 transition-colors" />
                    <p className="text-sm font-medium">Click to upload a cover image</p>
                    <p className="text-xs text-slate-400 mt-1">JPG, PNG, WEBP</p>
                  </div>
                ) : (
                  <div className="relative w-full h-48 sm:h-64 rounded-xl border border-slate-200 overflow-hidden group">
                    <img src={imagePreview} alt="Cover Preview" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button
                        type="button"
                        onClick={removeImage}
                        className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium shadow-sm transition-transform active:scale-95"
                      >
                        <X className="w-4 h-4" /> Remove Image
                      </button>
                    </div>
                  </div>
                )}
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleImageChange} 
                  accept="image/png, image/jpeg, image/webp" 
                  className="hidden" 
                />
              </div>

              <div>
                <label htmlFor="title" className="block text-sm font-semibold text-slate-700 mb-2">
                  Blog Title
                </label>
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., 5 Ways to Improve Your Daily Routine..."
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-shadow text-slate-900 placeholder:text-slate-400 font-medium"
                  required
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="content" className="block text-sm font-semibold text-slate-700">
                    Article Content
                  </label>
                  <span className="text-xs text-slate-400 font-medium">Markdown supported</span>
                </div>
                <textarea
                  id="content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Write your amazing article here..."
                  rows={12}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-shadow text-slate-900 placeholder:text-slate-400 resize-y"
                  required
                />
              </div>

              <div className="flex items-center justify-end pt-2 border-t border-slate-100 gap-3">
                {editingBlogId && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-6 py-3 text-slate-600 hover:bg-slate-100 font-semibold rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={!title.trim() || !content.trim() || isSubmitting}
                  className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 disabled:cursor-not-allowed text-white font-bold rounded-xl shadow-sm transition-all active:scale-95"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {loadingText || 'Processing...'}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      {editingBlogId ? 'Update & Submit for Review' : 'Submit for Approval'}
                    </>
                  )}
                </button>
              </div>

            </form>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
            {isLoadingBlogs ? (
              <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                <Loader2 className="w-8 h-8 animate-spin mb-3 text-blue-500" />
                <p>Loading your articles...</p>
              </div>
            ) : blogs.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10 text-center flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 border border-slate-100">
                  <BookOpen className="w-8 h-8 text-slate-300" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">No articles yet</h3>
                <p className="text-slate-500 text-sm mb-6 max-w-sm mx-auto">You haven't published any blogs. Write your first post to share your expertise with clients!</p>
                <button 
                  onClick={() => setActiveTab('write')}
                  className="text-blue-600 bg-blue-50 hover:bg-blue-100 font-semibold px-5 py-2.5 rounded-lg transition-colors"
                >
                  Write your first post
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {blogs.map((blog) => (
                  <div key={blog.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col overflow-hidden">
                    
                    {blog.cover_image || blog.coverImage ? (
                      <div className="w-full h-40 bg-slate-100 border-b border-slate-100">
                        <img src={blog.cover_image || blog.coverImage} alt={blog.title} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-full h-24 bg-gradient-to-br from-slate-50 to-slate-100 border-b border-slate-100 flex items-center justify-center">
                        <ImageIcon className="w-8 h-8 text-slate-300" />
                      </div>
                    )}

                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-bold text-lg text-slate-900 line-clamp-2 leading-tight pr-2">
                          {blog.title}
                        </h3>
                        
                        <div className="flex flex-col items-end gap-2 ml-2 shrink-0">
                          {blog.blog_status === 'published' || blog.status === 'published' ? (
                            <span className="flex items-center gap-1 bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md border border-emerald-100">
                              <CheckCircle2 className="w-3 h-3" /> Published
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 bg-amber-50 text-amber-700 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md border border-amber-100">
                              <Clock4 className="w-3 h-3" /> Pending
                            </span>
                          )}

                          <div className="flex items-center gap-1">
                            <button 
                              onClick={() => handleEditClick(blog)}
                              className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                              title="Edit Blog"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleDeleteClick(blog.id)}
                              disabled={isDeleting === blog.id}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                              title="Delete Blog"
                            >
                              {isDeleting === blog.id ? <Loader2 className="w-4 h-4 animate-spin text-red-500" /> : <Trash2 className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-slate-600 text-sm mb-5 line-clamp-3 leading-relaxed flex-1">
                        {blog.content}
                      </p>
                      
                      <div className="flex items-center justify-between pt-4 border-t border-slate-100 mt-auto">
                        <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
                          <Clock className="w-3.5 h-3.5" />
                          {new Date(blog.createdAt || blog.created_at || new Date()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                            <Eye className="w-4 h-4 text-slate-400" /> {Number(blog.views || 0).toLocaleString()}
                          </span>
                          <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                            <ThumbsUp className="w-4 h-4 text-slate-400" /> {Number(blog.likes || 0).toLocaleString()}
                          </span>
                        </div>
                      </div>

                      {/* 🟢 ADDED READ MORE BUTTON */}
                      <button 
                        onClick={() => navigate(`/blog/${blog.id}`)}
                        className="w-full mt-4 py-2 text-blue-600 hover:text-blue-700 font-semibold transition-colors flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 rounded-lg"
                      >
                        Read More
                        <ArrowRight className="w-4 h-4 transition-all" />
                      </button>
                      
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}